import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Truck,
  Settings,
  TruckIcon,
  FileText,
  ChevronDown,
  ChevronRight,
  LogOut,
  ClipboardList,
  BarChart3,
  Layers,
  LayoutDashboard,
  Radio,
  Search,
  FlaskConical,
  Users,
  User,
  MapPin,
  Inbox,
  Gavel,
  ShieldCheck,
  Building2,
  Factory,
  ScrollText,
  Shield,
  Bell,
  IndianRupee,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoMode } from '../../hooks/useDemoMode';
import { isDemoFeatureAllowed } from '../../config/featureFlags';
import { getRoleNavigation } from '../../config/roleWorkspace';
import { classificationBadge } from '../../config/navigation';
import { PLATFORM_NAV } from '../../config/platformNavigation';
import { GlobalSearch } from '../search/GlobalSearch';
import { Switch } from '../ui/Switch';
import { BrandLogo } from '../branding/BrandLogo';
import { isPlatformOwner } from '../../utils/platform';
import { useShellNav } from '../../contexts/ShellNavContext';
import { TenantSwitcher } from '../tenant/TenantSwitcher';

const SECTION_ICONS: Record<string, React.ReactNode> = {
  Core: <LayoutDashboard size={18} />,
  Transportation: <TruckIcon size={18} />,
  'Transportation (Preview)': <TruckIcon size={18} />,
  Platform: <Shield size={18} />,
  Organization: <Layers size={18} />,
  Planning: <Inbox size={18} />,
  Operations: <ClipboardList size={18} />,
  Dispatch: <Truck size={18} />,
  Finance: <IndianRupee size={18} />,
  Approvals: <ShieldCheck size={18} />,
  'My work': <LayoutDashboard size={18} />,
  'Read only': <LayoutDashboard size={18} />,
};

const ITEM_ICONS: Record<string, React.ReactNode> = {
  Dashboard: <LayoutDashboard size={16} />,
  Dockets: <ClipboardList size={16} />,
  Tracking: <Search size={16} />,
  Carriers: <Truck size={16} />,
  Billing: <FileText size={16} />,
  'Billing & invoices': <FileText size={16} />,
  Analytics: <BarChart3 size={16} />,
  Reports: <FileText size={16} />,
  'Commercial Analytics': <BarChart3 size={16} />,
  Customers: <Users size={16} />,
  'Customer master': <Building2 size={16} />,
  'MDR queue': <Inbox size={16} />,
  'Transport vendors': <Factory size={16} />,
  'Vendor master': <Factory size={16} />,
  'Vendor quotes': <Gavel size={16} />,
  'Approval inbox': <ShieldCheck size={16} />,
  'Pending approval': <ShieldCheck size={16} />,
  'Live Tracking': <Radio size={16} />,
  Fleet: <Truck size={16} />,
  'Route Planning': <MapPin size={16} />,
  Drivers: <User size={16} />,
  Routes: <MapPin size={16} />,
  Trips: <MapPin size={16} />,
  'My trips': <MapPin size={16} />,
  Users: <Users size={16} />,
  Branches: <Layers size={16} />,
  Settings: <Settings size={16} />,
  'Payment modes': <IndianRupee size={16} />,
  'Audit log': <ScrollText size={16} />,
  'Audit Log': <ScrollText size={16} />,
  Notifications: <Bell size={16} />,
  'Contract Master': <FileText size={16} />,
  'Dispatch board': <Truck size={16} />,
  Tenants: <Building2 size={16} />,
};

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, membershipRole, hasActiveTenant } = useAuth();
  const { isDemoMode, setDemoMode } = useDemoMode();
  const { isSidebarOpen: isOpen, closeSidebar } = useShellNav();
  const roleNav = getRoleNavigation(membershipRole);
  const platformWorkspace = location.pathname.startsWith('/platform');
  const platformOwner = isPlatformOwner(user);
  const showSettings = !platformWorkspace && (membershipRole === 'OWNER' || membershipRole === 'ADMIN');
  const [expandedSections, setExpandedSections] = React.useState<string[]>(
    platformWorkspace ? ['Platform'] : roleNav.map((s) => s.name),
  );

  // Close drawer on navigation (mobile) — from origin/main responsive work
  React.useEffect(() => {
    closeSidebar();
  }, [location.pathname, location.search, closeSidebar]);

  // Escape closes the mobile drawer
  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSidebar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeSidebar]);

  React.useEffect(() => {
    if (platformWorkspace) {
      setExpandedSections(['Platform']);
    } else {
      setExpandedSections(roleNav.map((s) => s.name));
    }
  }, [platformWorkspace, membershipRole]);

  const handleDemoToggle = (enabled: boolean) => {
    if (enabled) {
      setDemoMode(true);
      navigate('/demo');
      return;
    }
    setDemoMode(false);
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName) ? prev.filter((s) => s !== sectionName) : [...prev, sectionName],
    );
  };

  /** Persistent active styles — colors only, no glow flicker. */
  const navItemClass = (active: boolean) =>
    `group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
      active
        ? 'bg-[#F98C10] text-white shadow-sm [&_svg]:text-white'
        : 'text-white/75 hover:bg-white/10 hover:text-white [&_svg]:text-white/70 hover:[&_svg]:text-white'
    }`;

  const sectionButtonClass =
    'group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-white/80 transition-colors duration-200 hover:bg-white/10 hover:text-white';

  const isNavItemActive = (path: string) => {
    const [pathname, query = ''] = path.split('?');
    const allowNestedDetail = [
      '/dockets',
      '/planning/requisitions',
      '/billing',
      '/settings/customers',
      '/settings/vendors',
      '/fleet',
      '/notifications',
    ].includes(pathname);

    const onPath = allowNestedDetail
      ? location.pathname === pathname || location.pathname.startsWith(`${pathname}/`)
      : location.pathname === pathname;
    if (!onPath) return false;

    if (!query) {
      if (pathname === '/fleet') {
        const tab = new URLSearchParams(location.search).get('tab');
        return !tab || tab === 'vehicles';
      }
      return true;
    }

    if (location.pathname !== pathname) return false;
    const params = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);
    for (const [key, value] of params.entries()) {
      if (current.get(key) !== value) return false;
    }
    return true;
  };

  const isPlatformNavActive = (path: string) => {
    if (path === '/platform') return location.pathname === '/platform';
    if (path === '/platform/tenants') {
      return (
        location.pathname === '/platform/tenants' ||
        location.pathname.startsWith('/platform/tenants/')
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      {/* Backdrop only — hamburger lives in AppNavbar to avoid overlapping the page title */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm transition-opacity md:hidden"
          onClick={closeSidebar}
          aria-hidden
        />
      )}

      <aside
        id="app-sidebar"
        className={`
          fixed left-0 top-0 z-40 flex h-screen w-64 flex-col border-r border-white/10 bg-[#2D1537]
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Product branding — same mark for platform and tenant consoles */}
        <div className="flex shrink-0 flex-col items-center justify-center px-3 pb-3 pt-4">
          <BrandLogo variant="full" surface="dark" size="lg" imgClassName="mx-auto" />
          {platformWorkspace && (
            <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.16em] text-white/45">
              Platform Console
            </p>
          )}
        </div>

        {/* Search (tenant workspace only — platform has no global entity search) */}
        {!platformWorkspace && (
          <div className="shrink-0 px-3 pb-2 pt-1">
            <GlobalSearch />
          </div>
        )}

        {/* Navigation */}
        <nav className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-4 pt-2">
          {platformWorkspace ? (
            <div>
              <button
                type="button"
                onClick={() => toggleSection('Platform')}
                className={sectionButtonClass}
              >
                <span>{SECTION_ICONS.Platform}</span>
                <span className="flex-1 text-left">Platform</span>
                {expandedSections.includes('Platform') ? (
                  <ChevronDown size={14} className="text-white/50" />
                ) : (
                  <ChevronRight size={14} className="text-white/50" />
                )}
              </button>

              {expandedSections.includes('Platform') && (
                <div className="ml-2 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                  {PLATFORM_NAV.map((item) => {
                    const active = isPlatformNavActive(item.path);
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/platform'}
                        onClick={closeSidebar}
                        aria-current={active ? 'page' : undefined}
                        className={navItemClass(active)}
                      >
                        <span>{ITEM_ICONS[item.name] ?? <ClipboardList size={16} />}</span>
                        <span className="flex-1 truncate">{item.name}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            roleNav.map((section) => (
              <div key={section.name} className="space-y-0.5">
                <button
                  type="button"
                  onClick={() => toggleSection(section.name)}
                  className={sectionButtonClass}
                >
                  <span>{SECTION_ICONS[section.name] ?? <TruckIcon size={18} />}</span>
                  <span className="flex-1 text-left">{section.name}</span>
                  {expandedSections.includes(section.name) ? (
                    <ChevronDown size={14} className="text-white/50" />
                  ) : (
                    <ChevronRight size={14} className="text-white/50" />
                  )}
                </button>

                {expandedSections.includes(section.name) && (
                  <div className="ml-2 space-y-0.5 border-l border-white/10 pl-2">
                    {section.items.map((item) => {
                      const badge = classificationBadge(item.classification);
                      const active = isNavItemActive(item.path);
                      return (
                        <NavLink
                          key={`${section.name}-${item.name}-${item.path}`}
                          to={item.path}
                          onClick={closeSidebar}
                          aria-current={active ? 'page' : undefined}
                          className={navItemClass(active)}
                        >
                          <span>{ITEM_ICONS[item.name] ?? <ClipboardList size={16} />}</span>
                          <span className="flex-1 truncate">{item.name}</span>
                          {badge && (
                            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/55">
                              {badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </nav>

        {/* Footer: demo toggle, utility links, sign out */}
        <div className="shrink-0 space-y-3 border-t border-white/10 px-3 pb-4 pt-3">
          {!platformWorkspace && isDemoFeatureAllowed() && (
            <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5">
              <div className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-white/80">
                <FlaskConical size={15} className="shrink-0 text-amber-300/90" />
                <span className="truncate">Demo Mode</span>
              </div>
              <Switch
                checked={isDemoMode}
                onCheckedChange={handleDemoToggle}
                aria-label="Toggle Demo Mode"
              />
            </div>
          )}

          {platformOwner && !platformWorkspace && (
            <NavLink
              to="/platform"
              onClick={closeSidebar}
              aria-current={location.pathname.startsWith('/platform') ? 'page' : undefined}
              className={navItemClass(location.pathname.startsWith('/platform'))}
            >
              <Shield size={16} />
              <span>Platform Console</span>
            </NavLink>
          )}

          {platformWorkspace && hasActiveTenant && (
            <NavLink
              to="/dashboard"
              onClick={closeSidebar}
              className={navItemClass(false)}
            >
              <LayoutDashboard size={16} />
              <span>Back to workspace</span>
            </NavLink>
          )}

          {showSettings && (
            <NavLink
              to="/settings"
              onClick={closeSidebar}
              aria-current={isNavItemActive('/settings') ? 'page' : undefined}
              className={navItemClass(isNavItemActive('/settings'))}
            >
              <Settings size={16} />
              <span>Settings & Members</span>
            </NavLink>
          )}

          {!platformWorkspace && <TenantSwitcher variant="compact" />}

          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-[13px] font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Sign out"
          >
            <LogOut size={15} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
